import { useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  deal_id: string | null;
  metadata: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

const TYPE_ICONS: Record<string, string> = {
  text_received: "\ud83d\udcf1",
  call_received: "\ud83d\udcde",
  voicemail: "\ud83d\udcde",
  deal_assigned: "\ud83c\udfe0",
  stage_changed: "\u2192",
  approval_needed: "\u2705",
  approval_result: "\ud83d\udccb",
  sla_warning: "\u26a0\ufe0f",
  build_scheduled: "\ud83d\udcc5",
  payment_received: "\ud83d\udcb5",
  spif_update: "\ud83c\udfc6",
  scoreboard: "\ud83e\udd47",
  system: "\u2699\ufe0f",
  pipeline_alert: "\ud83d\udea8",
  commission: "\ud83d\udcb0",
  // JAC's invoice differed from the sales order and Shannon adjusted it in Books (Oct 29, 2026; to Chad)
  trade_invoice_discrepancy: "\ud83e\uddfe",
};

const TYPE_COLORS: Record<string, string> = {
  text_received: "bg-blue-50 text-blue-600",
  call_received: "bg-purple-50 text-purple-600",
  voicemail: "bg-purple-50 text-purple-600",
  deal_assigned: "bg-emerald-50 text-emerald-600",
  stage_changed: "bg-slate-100 text-slate-600",
  approval_needed: "bg-amber-50 text-amber-600",
  approval_result: "bg-emerald-50 text-emerald-600",
  sla_warning: "bg-red-50 text-red-600",
  build_scheduled: "bg-indigo-50 text-indigo-600",
  payment_received: "bg-green-50 text-green-600",
  spif_update: "bg-amber-50 text-amber-600",
  scoreboard: "bg-amber-50 text-amber-600",
  system: "bg-slate-100 text-slate-500",
  pipeline_alert: "bg-red-50 text-red-600",
  commission: "bg-green-50 text-green-600",
  trade_invoice_discrepancy: "bg-amber-50 text-amber-700",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function NotificationBell() {
  const { member } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Panel geometry, measured against the real viewport. See `place()`.
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxH: number } | null>(null);

  // Load recent notifications
  const loadNotifications = useCallback(async () => {
    if (!member) return;
    setLoading(true);
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", member.id)
      .order("created_at", { ascending: false })
      .limit(20);
    if (data) setNotifications(data as Notification[]);
    setLoading(false);
  }, [member]);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    if (!member) return;
    const { count } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", member.id)
      .eq("is_read", false);
    setUnreadCount(count ?? 0);
  }, [member]);

  // Initial load
  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
  }, [loadNotifications, loadUnreadCount]);

  // Real-time subscription for instant updates
  useEffect(() => {
    if (!member) return;

    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${member.id}`,
        },
        (payload) => {
          const newNotif = payload.new as Notification;
          setNotifications((prev) => [newNotif, ...prev].slice(0, 20));
          setUnreadCount((c) => c + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [member]);

  // Measure where the panel should sit.
  //
  // The panel is portaled to <body> (see the render block) because the app
  // header carries `will-change: transform` from useVisualViewportPin, which
  // makes it a STACKING CONTEXT. Rendered in place, the panel's z-50 only
  // competed inside the header — and on mobile the header wrapper has no
  // z-index (`sm:z-40` is desktop-only), so any positioned element in <main>
  // painted straight over the dropdown. Escaping to <body> also restores
  // `position: fixed` against the real viewport (a transformed ancestor would
  // otherwise become the containing block).
  //
  // Because it's fixed, we position it by hand: right-aligned to the bell,
  // then clamped inside the viewport. The bell sits well inboard of the right
  // edge (Sign out follows it), so a plain `right-0` + fixed 384px panel hung
  // off the LEFT of a phone screen and clipped the "Notifications" heading.
  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const GUTTER = 8;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const width = Math.min(384 /* w-96 */, vw - GUTTER * 2);
    const top = b.bottom + 8;
    const left = Math.min(Math.max(b.right - width, GUTTER), vw - GUTTER - width);
    const maxH = Math.max(180, Math.min(vh * 0.7, vh - top - GUTTER));
    setPos({ top, left, width, maxH });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("resize", place);
    // Capture phase so the panel tracks the bell inside scrolling containers too.
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  // Close dropdown on outside click. The panel is portaled out of `ref`, so a
  // DOM-containment check has to test it separately or every click inside the
  // panel would read as "outside" and close it before the row's onClick fired.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const t = e.target as Node;
      const inTrigger = ref.current?.contains(t);
      const inPanel = panelRef.current?.contains(t);
      if (!inTrigger && !inPanel) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Mark single notification as read + navigate
  async function handleClick(n: Notification) {
    if (!n.is_read) {
      await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", n.id);
      setNotifications((prev) =>
        prev.map((p) => (p.id === n.id ? { ...p, is_read: true } : p))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    // Oct 29, 2026: a signed trade add-on opens the deal on that trade (metadata.url = .../deal/<id>?trade=<id>),
    // where Schedule is.
    const metaUrl = n.type === "trade_addendum_signed" && n.metadata && typeof n.metadata.url === "string" ? n.metadata.url : null;
    const metaPath = metaUrl ? metaUrl.replace(/^https:\/\/app\.bardinroofing\.com(?=\/)/, "") : null;
    if (metaPath && metaPath.startsWith("/")) {
      navigate(metaPath, { state: { from: location.pathname + location.search } });
      return;
    }
    if (n.deal_id) {
      const tabMap: Record<string, string> = {
        text_received: "comms",
        call_received: "comms",
        voicemail: "comms",
        coaching_note: "coaching",
        coaching_reply: "coaching",
        approval_needed: "agreement",
        approval_result: "agreement",
        payment_received: "stage",
        stage_changed: "stage",
        // the deal's Trades card (Overview), where the order's chip and Chad's price review sit
        trade_invoice_discrepancy: "overview",
      };
      const tab = tabMap[n.type] || "comms";
      navigate(`/deal/${n.deal_id}?tab=${tab}`, { state: { from: location.pathname + location.search } });
    } else {
      // No deal linked — go to full notifications page
      navigate("/notifications");
    }
  }

  // Mark all as read
  async function markAllRead() {
    if (!member) return;
    await supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq("user_id", member.id)
      .eq("is_read", false);
    setNotifications((prev) => prev.map((p) => ({ ...p, is_read: true })));
    setUnreadCount(0);
  }

  return (
    <div className="relative" ref={ref}>
      {/* Bell button */}
      <button
        ref={btnRef}
        onClick={() => {
          setOpen(!open);
          if (!open) loadNotifications();
        }}
        className="relative p-2 rounded-lg hover:bg-slate-700 transition"
        title="Notifications"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-bardin-orange text-white text-[10px] font-bold px-1 leading-none animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — portaled to <body>; see place() for why. */}
      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxH }}
          className="fixed bg-white rounded-xl shadow-2xl border border-slate-200 z-[60] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-bardin-ink text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-bardin-teal hover:underline"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/notifications");
                }}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                View all
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {loading && notifications.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400 animate-pulse">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 transition border-b border-slate-50 ${
                    !n.is_read ? "bg-bardin-sky/20" : ""
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${
                    TYPE_COLORS[n.type] ?? "bg-slate-100 text-slate-500"
                  }`}>
                    {TYPE_ICONS[n.type] ?? "\ud83d\udd14"}
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-sm leading-tight ${!n.is_read ? "font-semibold text-bardin-ink" : "text-slate-700"}`}>
                        {n.title}
                      </span>
                      {!n.is_read && (
                        <span className="w-2 h-2 rounded-full bg-bardin-teal flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    {n.body && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                    )}
                    {n.type === "voicemail" && n.metadata && typeof n.metadata.recording_url === "string" && (
                      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                        <audio controls preload="none" className="h-7 w-full max-w-[280px]">
                          <source src={n.metadata.recording_url} type="audio/mpeg" />
                        </audio>
                      </div>
                    )}
                    <span className="text-[10px] text-slate-400 mt-1 block">{timeAgo(n.created_at)}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
